import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, apiError, timeAgo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Mail, Send, Plus, ArrowLeft, Eye, Loader2, Users, Bell, Megaphone,
  Bold, Italic, Heading2, List, ListOrdered, Link2, ShieldCheck, Check,
} from "lucide-react";

const CATEGORIES = ["PRODUCT_UPDATE", "ANNOUNCEMENT", "OPERATIONAL", "MARKETING"];
const CATEGORY_LABELS = {
  PRODUCT_UPDATE: "Product update", ANNOUNCEMENT: "Announcement",
  OPERATIONAL: "Operational", MARKETING: "Marketing",
};
const AUDIENCES = ["ALL_USERS", "PROFESSIONALS", "CONSUMERS", "VERIFIED_PROFESSIONALS"];
const AUDIENCE_LABELS = {
  ALL_USERS: "All Users", PROFESSIONALS: "Professionals",
  CONSUMERS: "Consumers", VERIFIED_PROFESSIONALS: "Verified Professionals",
};
const STATUS_STYLE = {
  DRAFT: "bg-slate-100 text-slate-600", QUEUED: "bg-amber-100 text-amber-800",
  SENDING: "bg-blue-100 text-blue-700", SENT: "bg-emerald-100 text-emerald-700",
  PARTIALLY_FAILED: "bg-orange-100 text-orange-700", FAILED: "bg-red-100 text-red-700",
};

const Pill = ({ className = "", children, testid }) => (
  <span data-testid={testid} className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${className}`}>{children}</span>
);
const Card = ({ children, className = "" }) => (
  <div className={`bg-card border border-border rounded-xl ${className}`}>{children}</div>
);

/* ------------------------------ Rich text editor ------------------------------ */
function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || "";
  }, []); // eslint-disable-line
  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
    ref.current && ref.current.focus();
  };
  const addLink = () => {
    const url = window.prompt("Link URL (https:// only)");
    if (!url) return;
    if (!/^https:\/\//i.test(url)) { toast.error("Links must start with https://"); return; }
    exec("createLink", url);
  };
  const Tool = ({ icon: Icon, label, onClick }) => (
    <button type="button" data-testid={`rte-${label}`} onClick={onClick} title={label}
      className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 transition-colors">
      <Icon className="h-4 w-4" />
    </button>
  );
  return (
    <div className="rounded-xl border border-border overflow-hidden" data-testid="comms-editor">
      <div className="flex items-center gap-0.5 border-b border-border bg-slate-50 px-2 py-1">
        <Tool icon={Heading2} label="heading" onClick={() => exec("formatBlock", "H2")} />
        <Tool icon={Bold} label="bold" onClick={() => exec("bold")} />
        <Tool icon={Italic} label="italic" onClick={() => exec("italic")} />
        <Tool icon={List} label="bullets" onClick={() => exec("insertUnorderedList")} />
        <Tool icon={ListOrdered} label="numbers" onClick={() => exec("insertOrderedList")} />
        <Tool icon={Link2} label="link" onClick={addLink} />
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning data-testid="comms-editor-body"
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className="min-h-[200px] px-4 py-3 text-sm text-slate-800 leading-relaxed focus:outline-none prose prose-sm max-w-none"
        style={{ wordBreak: "break-word" }} />
    </div>
  );
}

/* ------------------------------ Compose ------------------------------ */
const emptyForm = {
  campaign_name: "", subject: "", category: "ANNOUNCEMENT",
  audience_type: "ALL_USERS", channels: ["EMAIL"], body_html: "",
  cta_label: "", cta_url: "",
};

function Compose({ initial, editingId, onExit, onSaved }) {
  const [form, setForm] = useState(initial || emptyForm);
  const [cid, setCid] = useState(editingId || null);
  const [count, setCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleChannel = (ch) => setForm((f) => ({
    ...f, channels: f.channels.includes(ch) ? f.channels.filter((c) => c !== ch) : [...f.channels, ch],
  }));

  useEffect(() => {
    let ok = true;
    setCountLoading(true);
    api.get("/admin/communications/audience-count", { params: { audience_type: form.audience_type } })
      .then((r) => ok && setCount(r.data.eligible_count))
      .catch(() => ok && setCount(null))
      .finally(() => ok && setCountLoading(false));
    return () => { ok = false; };
  }, [form.audience_type]);

  const payload = () => ({
    campaign_name: form.campaign_name.trim(), subject: form.subject.trim(),
    category: form.category, audience_type: form.audience_type, channels: form.channels,
    body_html: form.body_html, cta_label: form.cta_label.trim(), cta_url: form.cta_url.trim(),
  });

  const save = async () => {
    const p = payload();
    if (cid) { await api.patch(`/admin/communications/${cid}`, p); return cid; }
    const r = await api.post("/admin/communications", p);
    setCid(r.data.id);
    return r.data.id;
  };

  const doSaveDraft = async () => {
    setBusy(true);
    try { await save(); toast.success("Draft saved"); onSaved && onSaved(); onExit(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const doPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/communications/preview", {
        subject: form.subject, body_html: form.body_html,
        cta_label: form.cta_label, cta_url: form.cta_url, category: form.category,
      });
      setPreview(r.data); setPreviewOpen(true);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const doTest = async () => {
    setBusy(true);
    try { const id = await save(); await api.post(`/admin/communications/${id}/test`, {}); toast.success("Test email sent to your admin address"); onSaved && onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const doSend = async () => {
    setBusy(true);
    try {
      const id = await save();
      const r = await api.post(`/admin/communications/${id}/send`, {});
      toast.success(`Campaign queued for ${r.data.eligible_recipient_count} recipients`);
      setConfirmOpen(false); onSaved && onSaved(); onExit();
    } catch (e) { toast.error(apiError(e)); setConfirmOpen(false); } finally { setBusy(false); }
  };

  const canSend = form.campaign_name.trim() && form.subject.trim() && form.channels.length > 0
    && (!form.channels.includes("EMAIL") || form.body_html.trim());

  return (
    <div className="space-y-5" data-testid="comms-compose">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onExit} data-testid="comms-back" className="rounded-lg -ml-2">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h2 className="font-display text-xl font-semibold text-slate-900">{cid ? "Edit communication" : "New communication"}</h2>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Campaign name</Label>
          <Input data-testid="comms-name" placeholder="Connections Feature Announcement"
            value={form.campaign_name} onChange={(e) => set("campaign_name", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Email subject</Label>
          <Input data-testid="comms-subject" placeholder="Build your professional network with PropertyConnect"
            value={form.subject} onChange={(e) => set("subject", e.target.value)} />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Category</Label>
          <Select value={form.category} onValueChange={(v) => set("category", v)}>
            <SelectTrigger data-testid="comms-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`comms-category-${c}`}>{CATEGORY_LABELS[c]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Audience</Label>
          <Select value={form.audience_type} onValueChange={(v) => set("audience_type", v)}>
            <SelectTrigger data-testid="comms-audience"><SelectValue /></SelectTrigger>
            <SelectContent>
              {AUDIENCES.map((a) => <SelectItem key={a} value={a} data-testid={`comms-audience-${a}`}>{AUDIENCE_LABELS[a]}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-1" data-testid="comms-recipient-count">
            <Users className="h-3.5 w-3.5" />
            {countLoading ? "Calculating…" : count == null ? "—" : `This campaign will be sent to ${count} eligible recipients.`}
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Channels <span className="text-red-500">*</span></Label>
        <div className="flex flex-wrap gap-2.5">
          <button type="button" data-testid="comms-channel-EMAIL"
            onClick={() => toggleChannel("EMAIL")}
            className={`flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm transition-colors ${form.channels.includes("EMAIL") ? "border-[#0F2C59] bg-[#0F2C59]/5 text-[#0F2C59] font-medium" : "border-border text-slate-600 hover:bg-slate-50"}`}>
            <span className={`h-4 w-4 rounded border flex items-center justify-center ${form.channels.includes("EMAIL") ? "bg-[#0F2C59] border-[#0F2C59]" : "border-slate-300"}`}>
              {form.channels.includes("EMAIL") && <Check className="h-3 w-3 text-white" />}
            </span>
            <Mail className="h-4 w-4" /> Email
          </button>
          <button type="button" data-testid="comms-channel-IN_APP"
            onClick={() => toggleChannel("IN_APP")}
            className={`flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm transition-colors ${form.channels.includes("IN_APP") ? "border-[#0F2C59] bg-[#0F2C59]/5 text-[#0F2C59] font-medium" : "border-border text-slate-600 hover:bg-slate-50"}`}>
            <span className={`h-4 w-4 rounded border flex items-center justify-center ${form.channels.includes("IN_APP") ? "bg-[#0F2C59] border-[#0F2C59]" : "border-slate-300"}`}>
              {form.channels.includes("IN_APP") && <Check className="h-3 w-3 text-white" />}
            </span>
            <Bell className="h-4 w-4" /> In-App
          </button>
        </div>
        {form.channels.length === 0
          ? <p className="text-xs text-red-500" data-testid="comms-channel-error">Select at least one channel.</p>
          : <p className="text-xs text-slate-400" data-testid="comms-channel-hint">
              Sending via {form.channels.map((c) => (c === "EMAIL" ? "Email" : "In-App")).join(" + ")}.
            </p>}
      </div>

      <div className="space-y-1.5">
        <Label>Message</Label>
        <RichTextEditor value={form.body_html} onChange={(v) => set("body_html", v)} />
        <p className="text-xs text-slate-400">Use {"{{first_name}}"} to personalize. Falls back to “there” when unknown.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Button text (optional)</Label>
          <Input data-testid="comms-cta-label" placeholder="Explore Connections"
            value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Button destination (optional)</Label>
          <Input data-testid="comms-cta-url" placeholder="/connections or https://…"
            value={form.cta_url} onChange={(e) => set("cta_url", e.target.value)} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
        <Button variant="outline" onClick={doPreview} disabled={busy} data-testid="comms-preview-btn" className="rounded-lg">
          <Eye className="h-4 w-4 mr-1.5" /> Preview
        </Button>
        <Button variant="outline" onClick={doTest} disabled={busy} data-testid="comms-test-btn" className="rounded-lg">
          <Send className="h-4 w-4 mr-1.5" /> Send Test
        </Button>
        <div className="flex-1" />
        <Button variant="outline" onClick={doSaveDraft} disabled={busy} data-testid="comms-save-draft" className="rounded-lg">
          Save Draft
        </Button>
        <Button onClick={() => setConfirmOpen(true)} disabled={busy || !canSend} data-testid="comms-send-btn"
          className="rounded-lg bg-[#0F2C59] hover:bg-[#0B2247] text-white">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Send
        </Button>
      </div>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl" data-testid="comms-preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview — {preview?.subject}</DialogTitle>
            <DialogDescription>Desktop and mobile rendering of your branded email.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-[1fr_390px] gap-4 max-h-[70vh] overflow-auto">
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">Desktop</div>
              <iframe title="preview-desktop" data-testid="comms-preview-desktop" srcDoc={preview?.email_html}
                className="w-full h-[480px] border border-border rounded-lg bg-white" />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">Mobile (390px)</div>
              <iframe title="preview-mobile" data-testid="comms-preview-mobile" srcDoc={preview?.email_html}
                className="w-[390px] max-w-full h-[480px] border border-border rounded-lg bg-white" />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send confirmation */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="comms-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send this communication?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to send this communication to <b>{count ?? "…"}</b> eligible PropertyConnect users.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-lg bg-slate-50 border border-border p-3 text-sm space-y-1">
            <div><span className="text-slate-500">Campaign:</span> {form.campaign_name}</div>
            <div><span className="text-slate-500">Audience:</span> {AUDIENCE_LABELS[form.audience_type]}</div>
            <div><span className="text-slate-500">Recipients (unique users):</span> {count ?? "…"}</div>
            <div><span className="text-slate-500">Deliveries:</span> {count == null ? "…" : count * form.channels.length} across {form.channels.map((c) => (c === "EMAIL" ? "Email" : "In-App")).join(" + ")}</div>
            <div><span className="text-slate-500">Subject:</span> {form.subject}</div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="comms-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="comms-confirm-send" onClick={(e) => { e.preventDefault(); doSend(); }}
              className="bg-[#0F2C59] hover:bg-[#0B2247]">Confirm &amp; Send</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ------------------------------ Detail ------------------------------ */
function DetailDialog({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!id) { setD(null); return; }
    let alive = true;
    let timer = null;
    const fetchOnce = async () => {
      try {
        const r = await api.get(`/admin/communications/${id}`);
        if (!alive) return;
        setD(r.data);
        // Lightweight polling only while the campaign is actively delivering.
        if (["QUEUED", "SENDING"].includes(r.data.status)) {
          timer = setTimeout(fetchOnce, 2500);
        }
      } catch { /* ignore */ }
    };
    fetchOnce();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [id]);

  const s = d?.delivery_summary || {};
  const totalDeliveries = d?.delivery_count
    || (["QUEUED", "PROCESSING", "SENT", "FAILED", "SUPPRESSED"].reduce((a, k) => a + (s[k] || 0), 0));
  const done = (s.SENT || 0) + (s.FAILED || 0) + (s.SUPPRESSED || 0);
  const pct = totalDeliveries ? Math.round((done / totalDeliveries) * 100) : 0;
  const inProgress = d && ["QUEUED", "SENDING"].includes(d.status);

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="comms-detail-dialog">
        <DialogHeader>
          <DialogTitle>{d?.campaign_name || "Campaign"}</DialogTitle>
          <DialogDescription>{d?.subject}</DialogDescription>
        </DialogHeader>
        {d && (
          <div className="space-y-4 max-h-[70vh] overflow-auto">
            <div className="flex flex-wrap gap-2">
              <Pill className={STATUS_STYLE[d.status]}>{d.status}</Pill>
              <Pill className="bg-slate-100 text-slate-600">{CATEGORY_LABELS[d.category]}</Pill>
              <Pill className="bg-slate-100 text-slate-600">{AUDIENCE_LABELS[d.audience_type]}</Pill>
              {(d.channels || []).map((c) => <Pill key={c} className="bg-indigo-100 text-indigo-700">{c === "EMAIL" ? "Email" : "In-App"}</Pill>)}
            </div>

            {inProgress && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="comms-progress">
                <div className="flex items-center justify-between text-xs text-blue-800 mb-1.5">
                  <span className="flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Delivering…</span>
                  <span data-testid="comms-progress-label">{done} / {totalDeliveries} deliveries ({pct}%)</span>
                </div>
                <div className="h-2 w-full rounded-full bg-blue-100 overflow-hidden">
                  <div className="h-full bg-[#0F2C59] transition-all duration-500" style={{ width: `${pct}%` }} data-testid="comms-progress-bar" />
                </div>
                {(d.channels || []).includes("EMAIL") && done < totalDeliveries && (
                  <p className="text-[11px] text-blue-700/80 mt-1.5">Email is paced to respect provider limits — larger sends may take a few minutes to finish.</p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <Info label="Recipients (unique)" value={d.eligible_recipient_count} testid="comms-d-recipients" />
              <Info label="Deliveries" value={totalDeliveries || "—"} testid="comms-d-deliveries" />
              <Info label="Sent" value={s.SENT ?? d.sent_count} testid="comms-d-sent" />
              <Info label="Failed" value={s.FAILED ?? d.failed_count} testid="comms-d-failed" />
              <Info label="Suppressed" value={s.SUPPRESSED ?? d.suppressed_count} testid="comms-d-suppressed" />
              <Info label="In-App sent" value={d.inapp_sent_count} testid="comms-d-inapp" />
            </div>
            <div className="text-xs text-slate-400">Created by {d.created_by_name} · {timeAgo(d.created_at)}</div>

            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">Content preview</div>
              <div className="rounded-lg border border-border p-4 text-sm text-slate-800 prose prose-sm max-w-none"
                data-testid="comms-d-content" dangerouslySetInnerHTML={{ __html: d.body_html || "<em>No content</em>" }} />
              {d.cta_label && <p className="text-xs text-slate-500 mt-2">Button: <b>{d.cta_label}</b> → {d.cta_url}</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
const Info = ({ label, value, testid }) => (
  <div className="bg-slate-50 border border-border rounded-lg px-3 py-2" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="font-semibold text-slate-900 truncate">{value ?? "—"}</div>
  </div>
);

/* ------------------------------ List ------------------------------ */
export default function CommunicationsConsole() {
  const [view, setView] = useState("list");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [previewSafe, setPreviewSafe] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/communications", { params: { page: 1, page_size: 50 } })
      .then((r) => { setItems(r.data.items || []); setPreviewSafe(!!r.data.preview_safe); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setView("compose"); };
  const openEdit = (c) => {
    setEditing({
      initial: {
        campaign_name: c.campaign_name || "", subject: c.subject || "", category: c.category,
        audience_type: c.audience_type, channels: c.channels || ["EMAIL"],
        body_html: c.body_html || "", cta_label: c.cta_label || "", cta_url: c.cta_url || "",
      }, id: c.id,
    });
    setView("compose");
  };

  if (view === "compose") {
    return (
      <Compose initial={editing?.initial} editingId={editing?.id}
        onExit={() => { setView("list"); setEditing(null); }} onSaved={load} />
    );
  }

  return (
    <div className="space-y-5" data-testid="comms-console">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="eyebrow flex items-center gap-2"><Megaphone className="h-4 w-4" /> Communications</p>
          <h2 className="font-display text-2xl font-semibold text-slate-900">Communications</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">Create and send important PropertyConnect announcements and updates to your users.</p>
        </div>
        <Button onClick={openNew} data-testid="comms-new-btn" className="rounded-lg bg-[#0F2C59] hover:bg-[#0B2247] text-white">
          <Plus className="h-4 w-4 mr-1.5" /> New Communication
        </Button>
      </div>

      {previewSafe && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 flex items-center gap-2 text-xs text-emerald-800" data-testid="comms-safe-note">
          <ShieldCheck className="h-4 w-4 shrink-0" />
          Preview environment: bulk emails are routed to a safe sandbox address — real users are never emailed from Preview.
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center" >
          <div data-testid="comms-empty">
            <Mail className="h-8 w-8 text-slate-300 mx-auto mb-3" />
            <div className="font-semibold text-slate-800">No communications yet.</div>
            <p className="text-sm text-slate-500 mt-1">Create your first announcement for the PropertyConnect community.</p>
            <Button onClick={openNew} data-testid="comms-empty-create" className="rounded-lg mt-4 bg-[#0F2C59] hover:bg-[#0B2247] text-white">
              <Plus className="h-4 w-4 mr-1.5" /> Create Communication
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm" data-testid="comms-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Campaign</th>
                  <th className="text-left font-medium px-4 py-3">Audience</th>
                  <th className="text-left font-medium px-4 py-3">Recipients</th>
                  <th className="text-left font-medium px-4 py-3">Deliveries</th>
                  <th className="text-left font-medium px-4 py-3">Created</th>
                  <th className="text-left font-medium px-4 py-3">Sent</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="text-left font-medium px-4 py-3">Created By</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-t border-border hover:bg-slate-50/60" data-testid={`comms-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <button className="text-left font-medium text-slate-900 hover:text-[#0F2C59]" onClick={() => setDetailId(c.id)} data-testid={`comms-open-${c.id}`}>
                        {c.campaign_name}
                      </button>
                      <div className="text-xs text-slate-400 truncate max-w-[240px]">{c.subject}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{AUDIENCE_LABELS[c.audience_type]}</td>
                    <td className="px-4 py-3 text-slate-600">{c.eligible_recipient_count}</td>
                    <td className="px-4 py-3 text-slate-600">{c.delivery_count ? c.delivery_count : "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{timeAgo(c.created_at)}</td>
                    <td className="px-4 py-3 text-slate-600">{c.sent_count || 0}</td>
                    <td className="px-4 py-3"><Pill className={STATUS_STYLE[c.status]}>{c.status}</Pill></td>
                    <td className="px-4 py-3 text-slate-500">{c.created_by_name}</td>
                    <td className="px-4 py-3 text-right">
                      {c.status === "DRAFT"
                        ? <Button size="sm" variant="outline" className="rounded-lg" onClick={() => openEdit(c)} data-testid={`comms-edit-${c.id}`}>Edit</Button>
                        : <Button size="sm" variant="ghost" className="rounded-lg" onClick={() => setDetailId(c.id)}>View</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-border">
            {items.map((c) => (
              <div key={c.id} className="p-4" data-testid={`comms-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <button className="text-left" onClick={() => setDetailId(c.id)}>
                    <div className="font-medium text-slate-900">{c.campaign_name}</div>
                    <div className="text-xs text-slate-400">{c.subject}</div>
                  </button>
                  <Pill className={STATUS_STYLE[c.status]}>{c.status}</Pill>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-2">
                  <span>{AUDIENCE_LABELS[c.audience_type]}</span>
                  <span>{c.eligible_recipient_count} recipients</span>
                  {c.delivery_count ? <span>{c.delivery_count} deliveries</span> : null}
                  <span>Sent {c.sent_count || 0}</span>
                  <span>{timeAgo(c.created_at)}</span>
                </div>
                {c.status === "DRAFT" && (
                  <Button size="sm" variant="outline" className="rounded-lg mt-3" onClick={() => openEdit(c)} data-testid={`comms-edit-m-${c.id}`}>Edit</Button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <DetailDialog id={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
